import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  CircularProgress,
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { tenXService } from '../../../../../services/TenXService';
import { formatTimestamp } from '../util';
import { deploymentOutcome, formatDeploymentDuration } from '../configUtil';
import type { ConfigurationDeployment } from '../configTypes';
import styles from './recentDeployments.module.css';

const POLL_INTERVAL_MS = 5000;
const POLL_MAX_MS = 10 * 60 * 1000;

interface RecentDeploymentsProps {
  deviceName: string;
  // Changing this value reloads the list if the panel is open.
  refreshToken?: number;
}

interface LogState {
  open: boolean;
  loading: boolean;
  text: string | null;
  error: string | null;
}

const RecentDeployments: React.FC<RecentDeploymentsProps> = ({
  deviceName,
  refreshToken = 0,
}) => {
  const [deployments, setDeployments] = useState<ConfigurationDeployment[] | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [logs, setLogs] = useState<Record<string, LogState>>({});
  const [expanded, setExpanded] = useState<boolean>(false);
  const [pollingStopped, setPollingStopped] = useState<boolean>(false);
  const [refreshFailed, setRefreshFailed] = useState<boolean>(false);

  // Quiet refresh: no spinner and open logs are kept. On failure the last
  // rows stay on screen.
  const refresh = useCallback(
    async (isCancelled: () => boolean = () => false) => {
      try {
        const result = await tenXService.getRecentConfigurationDeployments(deviceName);
        if (isCancelled()) return;
        setDeployments(result);
        setRefreshFailed(false);
      } catch {
        if (!isCancelled()) setRefreshFailed(true);
      }
    },
    [deviceName]
  );

  // Poll while the panel is open, until the time cap is reached. Starts once
  // the initial load finishes so the two requests never overlap.
  useEffect(() => {
    if (!expanded || loading || error || pollingStopped) return undefined;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    const deadline = Date.now() + POLL_MAX_MS;

    const tick = async () => {
      if (Date.now() >= deadline) {
        setPollingStopped(true);
        return;
      }
      await refresh(() => cancelled);
      if (!cancelled) timer = setTimeout(tick, POLL_INTERVAL_MS);
    };

    timer = setTimeout(tick, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [expanded, loading, error, pollingStopped, refresh]);

  // Parent-triggered reload. A closed panel fetches fresh data when opened.
  const handledToken = useRef(refreshToken);
  useEffect(() => {
    if (refreshToken === handledToken.current) return;
    handledToken.current = refreshToken;
    if (!expanded || loading) return;
    setError(null);
    setPollingStopped(false);
    refresh();
  }, [refreshToken, expanded, loading, refresh]);

  const handleResume = () => {
    setPollingStopped(false);
    refresh();
  };

  const handleToggle = async (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded);
    if (!isExpanded) return; // only fetch when opening

    setLoading(true);
    setError(null);
    setPollingStopped(false);
    setRefreshFailed(false);
    setLogs({}); // drop any logs from a previous fetch
    try {
      const result = await tenXService.getRecentConfigurationDeployments(deviceName);
      setDeployments(result);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load deployments';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleLog = async (deploymentId: string) => {
    const existing = logs[deploymentId];

    // Already loaded (or in flight): just toggle visibility.
    if (existing && (existing.text !== null || existing.error || existing.loading)) {
      setLogs((prev) => ({
        ...prev,
        [deploymentId]: { ...prev[deploymentId], open: !prev[deploymentId].open },
      }));
      return;
    }

    setLogs((prev) => ({
      ...prev,
      [deploymentId]: { open: true, loading: true, text: null, error: null },
    }));
    try {
      const text = await tenXService.getConfigurationDeploymentLog(
        deviceName,
        deploymentId
      );
      if (text === null) {
        setLogs((prev) => ({
          ...prev,
          [deploymentId]: {
            open: true,
            loading: false,
            text: null,
            error: 'No log was recorded for this deployment.',
          },
        }));
        return;
      }
      setLogs((prev) => ({
        ...prev,
        [deploymentId]: { open: true, loading: false, text: text || '', error: null },
      }));
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to load deployment log';
      setLogs((prev) => ({
        ...prev,
        [deploymentId]: { open: true, loading: false, text: null, error: message },
      }));
    }
  };

  return (
    <Accordion className={styles.accordion} onChange={handleToggle}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="h6">Recent Configuration Deployments</Typography>
      </AccordionSummary>
      <AccordionDetails>
        {/* Polled every POLL_INTERVAL_MS while open, up to POLL_MAX_MS. */}
        <Box className={styles.statusNote}>
          <Typography variant="caption" color="text.secondary">
            {pollingStopped
              ? 'Auto-refresh stopped because this panel was open too long.'
              : 'Refreshing every few seconds. Stops automatically if left open too long.'}
            {refreshFailed && ' Last refresh failed; showing the last known status.'}
          </Typography>
          {pollingStopped && (
            <Button size="small" onClick={handleResume}>
              Resume
            </Button>
          )}
        </Box>

        {loading && (
          <Box className={styles.centered}>
            <CircularProgress size={24} />
          </Box>
        )}

        {!loading && error && <Alert severity="error">{error}</Alert>}

        {!loading && !error && deployments && deployments.length === 0 && (
          <Typography variant="body2">No configuration deployments found.</Typography>
        )}

        {!loading && !error && deployments && deployments.length > 0 && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Deployment ID</TableCell>
                <TableCell>Outcome</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Completed</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {deployments.map((deployment) => {
                const outcome = deploymentOutcome(deployment);
                const duration = formatDeploymentDuration(
                  deployment.created,
                  deployment.finished
                );
                const failed = outcome.color === 'error';
                const logState = logs[deployment.id];
                return (
                  <React.Fragment key={deployment.id}>
                    <TableRow>
                      <TableCell className={styles.idCell}>{deployment.id}</TableCell>
                      <TableCell>
                        <Chip label={outcome.label} color={outcome.color} size="small" />
                      </TableCell>
                      <TableCell>
                        {deployment.created ? formatTimestamp(deployment.created) : '—'}
                      </TableCell>
                      <TableCell>
                        {deployment.finished ? (
                          <>
                            {formatTimestamp(deployment.finished)}
                            {duration && (
                              <Typography variant="caption" color="text.secondary">
                                {' '}
                                ({duration})
                              </Typography>
                            )}
                          </>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {failed && (
                          <Button
                            size="small"
                            onClick={() => handleToggleLog(deployment.id)}
                          >
                            {logState?.open ? 'Hide log' : 'View log'}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                    {failed && (
                      <TableRow>
                        <TableCell className={styles.logCell} colSpan={5}>
                          <Collapse in={logState?.open ?? false} unmountOnExit>
                            <Box className={styles.logContainer}>
                              {logState?.loading && (
                                <Box className={styles.centered}>
                                  <CircularProgress size={20} />
                                </Box>
                              )}
                              {!logState?.loading && logState?.error && (
                                <Alert severity="info">{logState.error}</Alert>
                              )}
                              {!logState?.loading &&
                                !logState?.error &&
                                logState?.text !== null &&
                                logState?.text !== undefined && (
                                  <pre className={styles.logBox}>
                                    {logState.text || 'Log is empty.'}
                                  </pre>
                                )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default RecentDeployments;
